import { useState, useRef, useCallback } from 'react'

// the list stops this far above the table's bottom edge
const LIST_GAP = 6

// A table cell's inline list scrolls inside itself and is given the height left in the table below it,
// so opening it never makes the table scroll vertically. Sideways, the table is scrolled so the list clears
// the sticky first column (`stickyLeft` wide) and the table's right edge; the page itself is never scrolled.
export default function useFitListToTable(fallbackMaxHeight: number, stickyLeft = 0) {
  const selectRef = useRef<HTMLDivElement>(null)
  const [maxHeight, setMaxHeight] = useState<number | null>(null)

  const onShow = useCallback(() => {
    // the list mounts on the render after the open, so it is measured a frame later
    requestAnimationFrame(() => {
      const panel = selectRef.current?.querySelector<HTMLElement>('.rfd-ui-inline-select-options')
      const table = selectRef.current?.closest<HTMLElement>('[data-compare-items="table"]')
      if (null == panel || null == table) return

      // the table's visible bottom, above its horizontal scrollbar
      const tableBottom = table.getBoundingClientRect().top + table.clientTop + table.clientHeight
      const box = getComputedStyle(panel)
      const frame =
        'border-box' === box.boxSizing
          ? 0
          : panel.offsetHeight - panel.clientHeight + parseFloat(box.paddingTop) + parseFloat(box.paddingBottom)
      setMaxHeight(Math.max(0, Math.floor(tableBottom - panel.getBoundingClientRect().top - frame - LIST_GAP)))

      revealSideways(panel, table, stickyLeft)
    })
  }, [stickyLeft])

  return { selectRef, maxHeight: maxHeight ?? fallbackMaxHeight, onShow }
}

export function revealSideways(panel: HTMLElement, table: HTMLElement, stickyLeft: number): void {
  const tableLeft = table.getBoundingClientRect().left + table.clientLeft
  const visibleLeft = tableLeft + stickyLeft
  const visibleRight = tableLeft + table.clientWidth
  const { left, right } = panel.getBoundingClientRect()

  if (left < visibleLeft) table.scrollLeft -= visibleLeft - left
  else if (right > visibleRight) table.scrollLeft += Math.min(right - visibleRight, left - visibleLeft)
}
